<template>
  <div
    ref="container"
    class="text-layer"
    :style="containerStyle as any"
    @pointerdown="onPointerDown"
    @touchstart="onTouchStart"
  >
    <div ref="textContent" class="text-content" :style="textStyle as any">
      {{ text }}
    </div>

    <!-- Resize handles -->
    <div
      v-for="handle in (['top-left', 'top-right', 'bottom-left', 'bottom-right'] as ResizeHandle[])"
      :key="handle"
      class="resize-handle"
      :class="handle"
      @pointerdown.stop="initResize($event, handle)"
      @touchstart.stop.prevent="initTouchResize($event, handle)"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import { useCanvasUnits } from '@/composables/useCanvasUnits'
import {useDesignerStore} from "@/stores/designerStore.ts";

type ResizeHandle = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'

interface Props {
  id: string
  text: string
  font: string
  fontWeight: string
  fontSize: number // in pt
  color: string
  align: 'left' | 'center' | 'right' | 'justify'
  positionX: number
  positionY: number
  width: number // in mm
  height: number // in mm
  zIndex: number
}

const props = defineProps<Props>()
const emit = defineEmits(['update:positionX', 'update:positionY', 'update:width', 'update:height'])

const { mmToPx, pxToMm, ptToPx } = useCanvasUnits()

const container = ref<HTMLElement | null>(null)

const pos = reactive({
  x: mmToPx(props.positionX, 'x'),
  y: mmToPx(props.positionY, 'y'),
})

const sizePx = reactive({
  width: mmToPx(props.width, 'x'),
  height: mmToPx(props.height, 'y'),
})

watch(
  () => [props.positionX, props.positionY],
  ([x, y]) => {
    pos.x = mmToPx(x, 'x')
    pos.y = mmToPx(y, 'y')
  },
)

watch(
  () => [props.width, props.height],
  ([w, h]) => {
    sizePx.width = mmToPx(w, 'x')
    sizePx.height = mmToPx(h, 'y')
  },
)

// Drag
const dragOffset = reactive({ x: 0, y: 0 })
const isDragging = ref(false)

const onPointerDown = (e: PointerEvent) => {
  dragOffset.x = e.clientX - pos.x
  dragOffset.y = e.clientY - pos.y
  isDragging.value = true
  window.addEventListener('pointermove', onPointerMove)
  window.addEventListener('pointerup', onPointerUp)
}

const onPointerMove = (e: PointerEvent) => {
  if (!isDragging.value) return
  pos.x = e.clientX - dragOffset.x
  pos.y = e.clientY - dragOffset.y
}

const onPointerUp = () => {
  isDragging.value = false
  window.removeEventListener('pointermove', onPointerMove)
  window.removeEventListener('pointerup', onPointerUp)
  emit('update:positionX', +pxToMm(pos.x, 'x').toFixed(2))
  emit('update:positionY', +pxToMm(pos.y, 'y').toFixed(2))
}

// Touch Drag
const onTouchStart = (e: TouchEvent) => {
  const touch = e.touches[0]
  dragOffset.x = touch.clientX - pos.x
  dragOffset.y = touch.clientY - pos.y
  isDragging.value = true
  window.addEventListener('touchmove', onTouchMove)
  window.addEventListener('touchend', onTouchEnd)
}

const onTouchMove = (e: TouchEvent) => {
  const touch = e.touches[0]
  pos.x = touch.clientX - dragOffset.x
  pos.y = touch.clientY - dragOffset.y
}

const onTouchEnd = () => {
  isDragging.value = false
  window.removeEventListener('touchmove', onTouchMove)
  window.removeEventListener('touchend', onTouchEnd)
  emit('update:positionX', +pxToMm(pos.x, 'x').toFixed(2))
  emit('update:positionY', +pxToMm(pos.y, 'y').toFixed(2))
}

// Resize
const resizeState = reactive({
  active: false,
  startX: 0,
  startY: 0,
  startWidth: 0,
  startHeight: 0,
  startPosX: 0,
  startPosY: 0,
  handle: '' as ResizeHandle,
})

const initResize = (e: PointerEvent, handle: ResizeHandle) => {
  resizeState.active = true
  resizeState.startX = e.clientX
  resizeState.startY = e.clientY
  resizeState.startWidth = sizePx.width
  resizeState.startHeight = sizePx.height
  resizeState.startPosX = pos.x
  resizeState.startPosY = pos.y
  resizeState.handle = handle
  window.addEventListener('pointermove', onResizeMove)
  window.addEventListener('pointerup', onResizeEnd)
}

const onResizeMove = (e: PointerEvent) => {
  const dx = e.clientX - resizeState.startX
  const dy = e.clientY - resizeState.startY

  if (resizeState.handle.includes('left')) {
    sizePx.width = Math.max(40, resizeState.startWidth - dx)
    pos.x = resizeState.startPosX + dx
  } else {
    sizePx.width = Math.max(40, resizeState.startWidth + dx)
  }

  if (resizeState.handle.includes('top')) {
    sizePx.height = Math.max(20, resizeState.startHeight - dy)
    pos.y = resizeState.startPosY + dy
  } else {
    sizePx.height = Math.max(20, resizeState.startHeight + dy)
  }
}

const onResizeEnd = () => {
  resizeState.active = false
  window.removeEventListener('pointermove', onResizeMove)
  window.removeEventListener('pointerup', onResizeEnd)
  emit('update:width', +pxToMm(sizePx.width, 'x').toFixed(2))
  emit('update:height', +pxToMm(sizePx.height, 'y').toFixed(2))
  emit('update:positionX', +pxToMm(pos.x, 'x').toFixed(2))
  emit('update:positionY', +pxToMm(pos.y, 'y').toFixed(2))
}

const initTouchResize = (e: TouchEvent, handle: ResizeHandle) => {
  const touch = e.touches[0]
  resizeState.active = true
  resizeState.startX = touch.clientX
  resizeState.startY = touch.clientY
  resizeState.startWidth = sizePx.width
  resizeState.startHeight = sizePx.height
  resizeState.startPosX = pos.x
  resizeState.startPosY = pos.y
  resizeState.handle = handle
  window.addEventListener('touchmove', onTouchResizeMove)
  window.addEventListener('touchend', onTouchResizeEnd)
}

const onTouchResizeMove = (e: TouchEvent) => {
  const touch = e.touches[0]
  const dx = touch.clientX - resizeState.startX
  const dy = touch.clientY - resizeState.startY

  if (resizeState.handle.includes('left')) {
    sizePx.width = Math.max(40, resizeState.startWidth - dx)
    pos.x = resizeState.startPosX + dx
  } else {
    sizePx.width = Math.max(40, resizeState.startWidth + dx)
  }

  if (resizeState.handle.includes('top')) {
    sizePx.height = Math.max(20, resizeState.startHeight - dy)
    pos.y = resizeState.startPosY + dy
  } else {
    sizePx.height = Math.max(20, resizeState.startHeight + dy)
  }
}

const onTouchResizeEnd = () => {
  resizeState.active = false
  window.removeEventListener('touchmove', onTouchResizeMove)
  window.removeEventListener('touchend', onTouchResizeEnd)
  emit('update:width', +pxToMm(sizePx.width, 'x').toFixed(2))
  emit('update:height', +pxToMm(sizePx.height, 'y').toFixed(2))
  emit('update:positionX', +pxToMm(pos.x, 'x').toFixed(2))
  emit('update:positionY', +pxToMm(pos.y, 'y').toFixed(2))
}

// Styling
const containerStyle = computed(() => ({
  position: 'absolute',
  left: `${pos.x}px`,
  top: `${pos.y}px`,
  width: `${sizePx.width}px`,
  height: `${sizePx.height}px`,
  zIndex: props.zIndex,
  boxSizing: 'border-box',
  overflow: 'hidden',
  touchAction: 'none',
  border: '1px dashed rgba(0,0,0,0.2)',
}))

const textStyle = computed(() => ({
  fontFamily: props.font,
  fontWeight: props.fontWeight,
  fontSize: `${ptToPx(props.fontSize)}px`,
  color: props.color,
  textAlign: props.align,
  width: '100%',
  height: '100%',
  padding: '4px',
  wordWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  lineHeight: 1.2,
}))

function recomputeFromProps() {
  pos.x = mmToPx(props.positionX, 'x')
  pos.y = mmToPx(props.positionY, 'y')
  sizePx.width  = mmToPx(props.width, 'x')
  sizePx.height = mmToPx(props.height, 'y')
}

// keď sa zmení škála (plátno), prepočítaj z props -> px
watch(
  () => useDesignerStore().scaleTick,
  () => {
    if (!container.value) return;
    if (!isDragging.value && !resizeState.active) {
      recomputeFromProps();
    }
  }
)

// optionally, urob aj init okamžite:
recomputeFromProps()
</script>

<style scoped>
.text-content {
  user-select: none;
  pointer-events: none;
}

.resize-handle {
  width: 14px;
  height: 14px;
  background: white;
  border: 1px solid black;
  position: absolute;
  z-index: 10;
  touch-action: none;
}

.resize-handle.top-left {
  top: -6px;
  left: -6px;
  cursor: nwse-resize;
}
.resize-handle.top-right {
  top: -6px;
  right: -6px;
  cursor: nesw-resize;
}
.resize-handle.bottom-left {
  bottom: -6px;
  left: -6px;
  cursor: nesw-resize;
}
.resize-handle.bottom-right {
  bottom: -6px;
  right: -6px;
  cursor: nwse-resize;
}
</style>
